import React from "react"
import { useQuery } from "@rocicorp/zero/react"
import Placeholder from "@tiptap/extension-placeholder"
import { EditorContent, useEditor } from "@tiptap/react"
import {
	IconFolderOutline18 as FolderIcon,
	IconPlusOutline18 as PlusIcon,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { getDefaultExtensions } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import {
	Dialog,
	Form,
	FormInput,
	FormTextarea,
	NucleoFillIcon,
	ScrollArea,
	ScrollViewport,
	SubmitButton,
	typographyVariants,
	useZodForm,
} from "@productlane/ui"
import { InternalDialogFooter } from "@productlane/ui/src/components/internal/dialog"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { DEFAULT_EDITOR_PROPS } from "@/components/editor/editor-config"
import { EmojiSuggestion } from "@/components/editor/extensions/emoji"
import { MacroVariableSuggestion } from "@/components/editor/extensions/macro-variables"
import slashCommands from "@/components/editor/extensions/slash-commands/slash-commands"
import { PopoverButton } from "@/components/popover-button"
import { analytics } from "@/lib/analytics"
import { generateId } from "@/lib/id"
import { useSession } from "@/lib/session"
import { mutateOk, useZero } from "@/lib/zero"
import { SnippetFolderDialog } from "./snippet-folder-dialog"
import { SnippetFormatToolbar } from "./snippet-format-toolbar"
import { snippetSlashSuggestion } from "./snippet-slash-suggestion"

interface Props {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	data?: {
		title?: string
		html?: string
		id?: string
		folderId?: string | null
	}
}

const UNCATEGORIZED = "uncategorized"
const CREATE_FOLDER = "create-folder"

const CreateSnippetFormSchema = z.object({
	title: z.string().min(1),
	html: z.string().min(1),
})

export const CreateSnippetDialog: React.FC<Props> = React.memo(
	({ isOpen, onOpenChange, data }) => {
		const zero = useZero()

		const { id, html, title, folderId } = data || {}

		const [folders] = useQuery(queries.snippetFolders())
		const [selectedFolderId, setSelectedFolderId] = React.useState<string>(
			folderId ?? UNCATEGORIZED,
		)
		const [isFolderDialogOpen, setFolderDialogOpen] = React.useState(false)

		React.useEffect(() => {
			setSelectedFolderId(folderId ?? UNCATEGORIZED)
		}, [folderId, isOpen])

		const folderOptions = React.useMemo(
			() => [
				{
					label: (
						<div className="flex items-center gap-2">
							<FolderIcon className="text-gray-600 dark:text-gray-400" />
							<span>Uncategorized</span>
						</div>
					),
					value: UNCATEGORIZED,
				},
				...folders.map((folder) => ({
					label: (
						<div className="flex items-center gap-2">
							{folder.icon ? (
								<NucleoFillIcon
									icon={folder.icon}
									className="h-[18px] w-[18px] text-gray-600 dark:text-gray-400"
								/>
							) : (
								<FolderIcon className="text-gray-600 dark:text-gray-400" />
							)}
							<span>{folder.name}</span>
						</div>
					),
					value: folder.id,
				})),
				{
					label: (
						<div className="flex items-center gap-2">
							<PlusIcon className="text-gray-600 dark:text-gray-400" />
							<span>Create folder</span>
						</div>
					),
					value: CREATE_FOLDER,
				},
			],
			[folders],
		)

		const handleFolderChange = React.useCallback((value: string | null) => {
			if (value === CREATE_FOLDER) {
				setFolderDialogOpen(true)
				return
			}
			setSelectedFolderId(value ?? UNCATEGORIZED)
		}, [])

		const handleFolderDialogClose = React.useCallback(
			(open: boolean, createdFolder?: { id: string; name: string }) => {
				setFolderDialogOpen(open)
				if (!open && createdFolder) {
					setSelectedFolderId(createdFolder.id)
				}
			},
			[],
		)

		const actionText = id ? "Update macro" : "Create macro"

		const form = useZodForm({
			schema: CreateSnippetFormSchema,
			defaultValues: {
				title,
				html,
			},
		})

		const editor = useEditor(
			{
				content: "",
				extensions: [
					...getDefaultExtensions({}),
					EmojiSuggestion,
					MacroVariableSuggestion,
					Placeholder.configure({
						showOnlyWhenEditable: false,
						placeholder: "Add content, type [ for customer data...",
					}),
					slashCommands.configure({ suggestion: snippetSlashSuggestion }),
				],
				editable: true,
				editorProps: {
					...DEFAULT_EDITOR_PROPS,
					attributes: {
						...DEFAULT_EDITOR_PROPS.attributes,
						class: cn(
							"focus:outline-none",
							typographyVariants({ variant: "message" }),
						),
					},
				},
			},
			[],
		)

		// We update the html form property to enable form validation
		const editorHtml = editor?.getHTML()
		React.useEffect(() => {
			if (!editorHtml) return
			form.setValue("html", editorHtml)
		}, [editorHtml, form])

		React.useEffect(() => {
			if (title !== undefined) {
				form.setValue("title", title)
			}

			if (html !== undefined) {
				editor?.commands.setContent(html)
			}
		}, [editor, form, id, title, html])
		const { session } = useSession()

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				className="m-2 p-0"
				position="right"
				title={
					<>
						<div className="px-6 py-4">{actionText}</div>
						<hr />
					</>
				}
				withOverlay={false}
				onCloseAutoFocus={(event) => event.preventDefault()}
				onInteractOutside={(event) => event.preventDefault()}
				modal={false}
			>
				<ScrollArea>
					<ScrollViewport className="[&>div]:h-full">
						<Form
							id="create-snippet-form"
							form={form}
							className="items-between flex min-h-full flex-col justify-between"
							onSubmit={async (values, e) => {
								if (e?.isPropagationStopped()) return
								if (!session) return

								if (editor?.isEmpty) {
									form.setError("html", {
										message: "String must contain at least 1 character",
									})
									return
								}

								const newFolderId =
									selectedFolderId === UNCATEGORIZED ? null : selectedFolderId

								if (id) {
									await mutateOk(
										zero.mutate(
											mutators.snippets.update({
												id,
												title: values.title,
												html: values.html,
												folderId: newFolderId,
											}),
										),
									)
									analytics.track("snippet-update")
								} else {
									await mutateOk(
										zero.mutate(
											mutators.snippets.create({
												id: generateId(),
												title: values.title,
												html: values.html,
												emailId: null,
												folderId: newFolderId,
											}),
										),
									)
									analytics.track("snippet-create")
								}

								editor?.commands.setContent("")
								form.reset()
								onOpenChange(false)
							}}
						>
							<div className="flex flex-1 flex-col gap-3 px-6 pt-5">
								<div className="flex">
									<PopoverButton
										variant="outline"
										icon={FolderIcon}
										data={folderOptions}
										selected={selectedFolderId}
										onChange={handleFolderChange}
										placeholder="Search folders…"
										name="Select folder"
									/>
								</div>

								<FormInput
									textSize="xl"
									variant="borderless"
									name="title"
									placeholder="Macro name"
									className="mt-2"
								/>

								{/* Same typography and toolbar as the thread composer, so a
								    macro is written where it will be read. */}
								<div className="flex flex-1 cursor-text flex-col pt-1">
									<EditorContent
										editor={editor}
										className="flex-1 [&>.prose]:h-full [&_img]:!my-2"
									/>
									<div className="-ml-2 mt-2 flex items-center pb-2">
										<SnippetFormatToolbar editor={editor} />
									</div>
								</div>
								{/* We use the text area to show errors when editor is empty */}
								<FormTextarea name="html" hidden />
							</div>

							<div>
								<hr />
								<div className="px-6 py-4">
									<InternalDialogFooter>
										<SubmitButton form="create-snippet-form">
											{actionText}
										</SubmitButton>
									</InternalDialogFooter>
								</div>
							</div>
						</Form>
					</ScrollViewport>
				</ScrollArea>

				<SnippetFolderDialog
					isOpen={isFolderDialogOpen}
					onOpenChange={handleFolderDialogClose}
				/>
			</Dialog>
		)
	},
)
